import { stripeConfig } from '../config/stripeConfig';

// Types for payment operations
export interface StripeCustomer {
  id: string;
  email: string;
  name?: string;
  created: number;
  default_source?: string;
}

export interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  is_default: boolean;
}

export interface Subscription {
  id: string;
  customer: string;
  status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  items: {
    data: Array<{
      id: string;
      price: {
        id: string;
        unit_amount: number;
        currency: string;
        recurring: {
          interval: string;
        };
      };
    }>;
  };
}

export interface Invoice {
  id: string;
  customer: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  created: number;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
}

export interface CreateSubscriptionData {
  customerId: string;
  priceId: string;
  paymentMethodId?: string;
  trialPeriodDays?: number;
}

export interface UpdateSubscriptionData {
  subscriptionId: string;
  priceId: string;
  prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice';
}

class PaymentService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = stripeConfig.apiBaseUrl;
  }

  // Create a Stripe customer
  async createCustomer(email: string, name?: string, userId?: string): Promise<StripeCustomer> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/create-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name,
          userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create customer: ${response.statusText}`);
      }

      const data = await response.json();
      return data.customer;
    } catch (error) {
      console.error('Error creating Stripe customer:', error);
      throw new Error('Failed to create customer');
    }
  }

  // Get customer by ID
  async getCustomer(customerId: string): Promise<StripeCustomer> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/customer/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get customer: ${response.statusText}`);
      }

      const data = await response.json();
      return data.customer;
    } catch (error) {
      console.error('Error getting Stripe customer:', error);
      throw new Error('Failed to get customer');
    }
  }

  // Create a subscription
  async createSubscription(data: CreateSubscriptionData): Promise<Subscription> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to create subscription: ${response.statusText}`);
      }

      const result = await response.json();
      return result.subscription;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw new Error('Failed to create subscription');
    }
  }

  // Update a subscription
  async updateSubscription(data: UpdateSubscriptionData): Promise<Subscription> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/update-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to update subscription: ${response.statusText}`);
      }

      const result = await response.json();
      return result.subscription;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw new Error('Failed to update subscription');
    }
  }

  // Cancel a subscription
  async cancelSubscription(subscriptionId: string, immediately: boolean = false): Promise<Subscription> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId,
          immediately,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.statusText}`);
      }

      const result = await response.json();
      return result.subscription;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  }

  // Get subscription by ID
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/subscription/${subscriptionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get subscription: ${response.statusText}`);
      }

      const data = await response.json();
      return data.subscription;
    } catch (error) {
      console.error('Error getting subscription:', error);
      throw new Error('Failed to get subscription');
    }
  }

  // Get customer's payment methods
  async getPaymentMethods(customerId: string): Promise<PaymentMethod[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/payment-methods/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get payment methods: ${response.statusText}`);
      }

      const data = await response.json();
      return data.paymentMethods;
    } catch (error) {
      console.error('Error getting payment methods:', error);
      throw new Error('Failed to get payment methods');
    }
  }

  // Add a payment method to customer
  async addPaymentMethod(customerId: string, paymentMethodId: string): Promise<PaymentMethod> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/add-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          paymentMethodId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add payment method: ${response.statusText}`);
      }

      const data = await response.json();
      return data.paymentMethod;
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw new Error('Failed to add payment method');
    }
  }

  // Remove a payment method
  async removePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/remove-payment-method`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentMethodId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to remove payment method: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      throw new Error('Failed to remove payment method');
    }
  }

  // Get customer's invoices
  async getInvoices(customerId: string, limit: number = 10): Promise<Invoice[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/invoices/${customerId}?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get invoices: ${response.statusText}`);
      }

      const data = await response.json();
      return data.invoices;
    } catch (error) {
      console.error('Error getting invoices:', error);
      throw new Error('Failed to get invoices');
    }
  }

  // Create payment intent for one-time payments
  async createPaymentIntent(amount: number, currency: string, customerId?: string): Promise<{ clientSecret: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(amount * 100), // Convert to cents
          currency: currency.toLowerCase(),
          customerId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create payment intent: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw new Error('Failed to create payment intent');
    }
  }

  // Setup intent for saving payment methods
  async createSetupIntent(customerId: string): Promise<{ clientSecret: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create setup intent: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw new Error('Failed to create setup intent');
    }
  }
}

export const paymentService = new PaymentService();

